import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { MaskedLine, Eyebrow } from "../components/Reveal";
import { MagneticButton } from "../components/MagneticButton";
import { scrollToSection } from "../hooks/useLenis";

const WORDS = ["BUSINESSES", "MINISTRIES", "ENTREPRENEURS", "CREATORS", "VISIONARIES"];

const RotatingWord = ({ play }) => {
    const [i, setI] = useState(0);
    useEffect(() => {
        if (!play) return undefined;
        const t = setInterval(() => setI((v) => (v + 1) % WORDS.length), 2400);
        return () => clearInterval(t);
    }, [play]);
    return (
        <span className="relative inline-flex h-[1.2em] min-w-[8ch] overflow-hidden align-bottom">
            <AnimatePresence mode="wait">
                <motion.span
                    key={WORDS[i]}
                    className="text-electric-grad inline-block whitespace-nowrap"
                    initial={{ y: "110%", opacity: 0 }}
                    animate={{ y: "0%", opacity: 1 }}
                    exit={{ y: "-110%", opacity: 0 }}
                    transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                >
                    {WORDS[i]}
                </motion.span>
            </AnimatePresence>
        </span>
    );
};

const MiniSite = () => (
    <div className="flex h-full flex-col overflow-hidden rounded-lg bg-[#0A0F1D]">
        <div className="flex items-center gap-2 border-b border-white/10 bg-white/5 px-3 py-2">
            <span className="h-2 w-2 rounded-full bg-[#FF5F57]" />
            <span className="h-2 w-2 rounded-full bg-[#FEBC2E]" />
            <span className="h-2 w-2 rounded-full bg-[#28C840]" />
            <span className="ml-2 flex-1 rounded-full bg-white/5 px-3 py-0.5 font-mono text-[8px] text-slate-400">
                yourwebsite.com
            </span>
        </div>
        <div className="flex-1 p-4">
            <div className="mb-4 flex items-center justify-between">
                <span className="h-2.5 w-2.5 rounded-full bg-sky-400" />
                <div className="flex gap-2">
                    <span className="h-1 w-8 rounded bg-white/20" />
                    <span className="h-1 w-8 rounded bg-white/20" />
                    <span className="h-1 w-8 rounded bg-white/20" />
                </div>
            </div>
            <motion.div
                className="mb-2 h-3.5 w-3/4 rounded bg-gradient-to-r from-slate-100 to-slate-400"
                initial={{ scaleX: 0, originX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 3.2, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            />
            <motion.div
                className="mb-3 h-3.5 w-1/2 rounded bg-gradient-to-r from-sky-400 to-[#0066FF]"
                initial={{ scaleX: 0, originX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 3.4, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            />
            <motion.div
                className="mb-5 h-5 w-20 rounded-full bg-[#0066FF] shadow-[0_0_16px_rgba(0,102,255,0.5)]"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 3.7, duration: 0.5 }}
            />
            <div className="grid grid-cols-3 gap-2.5">
                {[0, 1, 2].map((c) => (
                    <motion.div
                        key={c}
                        className="rounded-md border border-white/10 bg-white/5 p-2"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 3.9 + c * 0.12, duration: 0.5 }}
                    >
                        <div className="mb-1.5 h-5 rounded bg-gradient-to-br from-sky-400/40 to-[#0066FF]/40" />
                        <div className="h-1 w-full rounded bg-white/15" />
                        <div className="mt-1 h-1 w-2/3 rounded bg-white/15" />
                    </motion.div>
                ))}
            </div>
        </div>
    </div>
);

const Laptop3D = () => {
    const reduce = useReducedMotion();
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rotateX = useSpring(my, { stiffness: 90, damping: 18 });
    const rotateY = useSpring(mx, { stiffness: 90, damping: 18 });

    const onMove = (e) => {
        if (reduce) return;
        const r = e.currentTarget.getBoundingClientRect();
        mx.set(((e.clientX - r.left) / r.width - 0.5) * 14);
        my.set(-((e.clientY - r.top) / r.height - 0.5) * 10);
    };

    return (
        <div onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }} style={{ perspective: 1400 }} className="relative">
            <div className="absolute -inset-10 rounded-full bg-[#0066FF]/15 blur-3xl" aria-hidden="true" />
            <motion.div
                style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
                className={reduce ? "relative" : "relative animate-float-soft"}
            >
                <div className="rounded-2xl border border-white/15 bg-gradient-to-b from-slate-800 to-slate-900 p-2 shadow-[0_40px_90px_rgba(0,0,0,0.6)]">
                    <div className="aspect-[16/10] overflow-hidden rounded-lg">
                        <MiniSite />
                    </div>
                </div>
                <div className="mx-auto mt-0 h-3.5 w-[112%] -translate-x-[6%] rounded-b-2xl rounded-t-sm bg-gradient-to-b from-slate-700 to-slate-900 shadow-[0_18px_40px_rgba(0,102,255,0.25)]" />
                <motion.div
                    className="absolute -right-4 -top-5 rounded-full glass px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#0066FF]"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 4.2, duration: 0.6 }}
                >
                    Mobile-First
                </motion.div>
                <motion.div
                    className="absolute -bottom-5 -left-6 rounded-full glass px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#0066FF]"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 4.4, duration: 0.6 }}
                >
                    SEO Ready
                </motion.div>
            </motion.div>
        </div>
    );
};

export const Hero = ({ play }) => {
    const reduce = useReducedMotion();
    const sectionRef = useRef(null);
    const { scrollY } = useScroll();
    const orbY = useTransform(scrollY, [0, 600], [0, reduce ? 0 : 140]);
    const fade = useTransform(scrollY, [0, 420], [1, 0]);

    const { scrollYProgress: exit } = useScroll({
        target: sectionRef,
        offset: ["start start", "end start"],
    });
    const contentY = useTransform(exit, [0, 1], [0, -140]);
    const contentOpacity = useTransform(exit, [0, 0.75], [1, 0]);
    const contentScale = useTransform(exit, [0, 1], [1, 0.93]);
    const laptopY = useTransform(exit, [0, 1], [0, 170]);
    const laptopRotate = useTransform(exit, [0, 1], [0, 5]);
    const laptopOpacity = useTransform(exit, [0, 0.8], [1, 0]);
    const contentStyle = reduce ? {} : { y: contentY, opacity: contentOpacity, scale: contentScale };
    const laptopStyle = reduce ? {} : { y: laptopY, rotate: laptopRotate, opacity: laptopOpacity };

    return (
        <section ref={sectionRef} id="hero" className="relative flex min-h-screen items-center overflow-hidden pt-[72px]" data-testid="hero-section">
            <div className="bg-grid-faint absolute inset-0" aria-hidden="true" />
            <motion.div style={{ y: orbY }} className="absolute inset-0" aria-hidden="true">
                <div className="absolute -top-32 right-[8%] h-[420px] w-[420px] rounded-full bg-[#0066FF]/12 blur-[120px]" />
                <div className="absolute bottom-[-10%] left-[-6%] h-[380px] w-[380px] rounded-full bg-sky-400/8 blur-[110px]" />
            </motion.div>
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#F7F9FC] to-transparent" aria-hidden="true" />

            <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-16 px-4 py-20 sm:px-6 lg:grid-cols-[1.3fr_0.9fr] lg:gap-6 lg:px-8">
                <motion.div style={contentStyle}>
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={play ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.7, delay: 0.15 }}
                    >
                        <Eyebrow>Footman Digital Solutions</Eyebrow>
                    </motion.div>

                    <h1 className="mt-7 font-display text-[clamp(2.4rem,5.4vw,4.5rem)] font-extrabold leading-[1.04] tracking-tight text-[#0A0F1D]">
                        <MaskedLine play={play} delay={0.25}>YOUR VISION</MaskedLine>
                        <MaskedLine play={play} delay={0.38}>DESERVES TO BE</MaskedLine>
                        <MaskedLine play={play} delay={0.51}>
                            <span className="text-electric-grad">ONLINE.</span>
                        </MaskedLine>
                    </h1>

                    <motion.p
                        className="mt-7 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
                        initial={{ opacity: 0, y: 20 }}
                        animate={play ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8, delay: 0.75 }}
                    >
                        We build modern websites that help businesses, ministries, and brands look
                        professional, reach more people, and create bigger opportunities.
                    </motion.p>

                    <motion.p
                        className="mt-5 font-display text-lg font-bold uppercase tracking-wide text-[#0A0F1D] sm:text-xl"
                        initial={{ opacity: 0, y: 20 }}
                        animate={play ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8, delay: 0.9 }}
                    >
                        We build for <RotatingWord play={play} />
                    </motion.p>

                    <motion.div
                        className="mt-9 flex flex-wrap items-center gap-4"
                        initial={{ opacity: 0, y: 20 }}
                        animate={play ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8, delay: 1.05 }}
                    >
                        <MagneticButton testid="hero-cta-build-website" onClick={() => scrollToSection("contact")}>
                            Let&apos;s Build Your Website <ArrowRight className="h-4 w-4" />
                        </MagneticButton>
                        <MagneticButton testid="hero-cta-view-work" variant="ghost" onClick={() => scrollToSection("work")}>
                            View Our Work
                        </MagneticButton>
                    </motion.div>

                    <motion.p
                        className="mt-10 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 sm:text-[11px]"
                        initial={{ opacity: 0 }}
                        animate={play ? { opacity: 1 } : {}}
                        transition={{ duration: 0.8, delay: 1.25 }}
                    >
                        We build for people with something to say, sell, build or change.
                    </motion.p>
                </motion.div>

                <motion.div style={laptopStyle} className="relative hidden lg:block">
                    <span className="sticker absolute -left-9 top-4 z-20 hidden xl:inline-block" data-testid="hero-sticker">No Templates. Ever.</span>
                    <motion.div
                        initial={{ opacity: 0, y: 60, scale: 0.94 }}
                        animate={play ? { opacity: 1, y: 0, scale: 1 } : {}}
                        transition={{ duration: 1.1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <Laptop3D />
                    </motion.div>
                </motion.div>
            </div>

            <motion.button
                data-testid="hero-scroll-indicator"
                onClick={() => scrollToSection("pain")}
                style={{ opacity: fade }}
                className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 font-mono text-[10px] uppercase tracking-[0.35em] text-slate-500 transition-colors hover:text-sky-400 md:flex"
                initial={{ opacity: 0 }}
                animate={play ? { opacity: 1 } : {}}
                transition={{ delay: 1.8 }}
                aria-label="Scroll to next section"
            >
                Scroll
                <motion.span animate={reduce ? {} : { y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
                    <ArrowDown className="h-4 w-4" />
                </motion.span>
            </motion.button>
        </section>
    );
};
